import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { t } from "@/i18n";

import { client, csrfHeader, unwrap } from "./client";
import type { components } from "./schema";

export type Company = components["schemas"]["Company"];
export type Branch = components["schemas"]["Branch"];
export type Contact = components["schemas"]["Contact"];
export type Activity = components["schemas"]["Activity"];

/**
 * Every list endpoint is `PageNumberPagination` with `PAGE_SIZE = 50`. It has to
 * be: the imported book is ~26k companies and ~223k activities, so nothing here
 * may ever fetch "all of them" (.docs/01b).
 */
export const PAGE_SIZE = 50;

/** A company's or contact's history, read a screenful at a time. The API's
 *  `page_size` shortens a page; it cannot lengthen one past `PAGE_SIZE`. */
export const TIMELINE_PAGE_SIZE = 15;

export type CompanyStatus = NonNullable<Company["status"]>;

/** Which book to list. See `CompanyViewSet._scope` for what the server does. */
export type CompanyScope = "mine" | "all";

export type CompanyFilters = {
  search: string;
  /** Empty string means "any". The value is a legacy status integer as text. */
  status: string;
  page: number;
  scope?: CompanyScope;
  /** A seller's id, or empty for everyone. Manager-only (design screen 02). */
  agent?: string;
};

export function useCompanies(filters: CompanyFilters) {
  return useQuery({
    queryKey: ["companies", filters],
    queryFn: () =>
      unwrap(
        client.GET("/api/companies/", {
          params: {
            query: {
              page: filters.page,
              ...(filters.scope ? { scope: filters.scope } : {}),
              ...(filters.search ? { search: filters.search } : {}),
              ...(filters.agent ? { current_agent: Number(filters.agent) } : {}),
              // The cast is narrow and checked at the boundary: `status` comes
              // from the URL, so it is a string that may be anything at all.
              // `statusFromParam` is what makes the assertion true.
              ...(filters.status
                ? { status: Number(filters.status) as CompanyStatus }
                : {}),
            },
          },
        }),
        t("errors.companies"),
      ),
    // Without this the table blanks out on every page change and every
    // keystroke of the search box, which reads as the data vanishing.
    placeholderData: keepPreviousData,
  });
}

/**
 * *Autoasignarme* — take an unassigned company (design screen 02, group 3).
 *
 * The 409 is not an error to swallow: it means a colleague got there first,
 * and the body carries the company as it now stands so the row can say who.
 * `unwrap` would turn it into a thrown message and lose that, so this one
 * reads the response itself.
 */
export function useClaimCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const { data, response } = await client.POST("/api/companies/{id}/claim/", {
        params: { path: { id } },
      });
      if (!data) throw new Error(t("errors.claim"));
      return { company: data, lost: response.status === 409 };
    },
    // Whoever won, the list is now wrong: the row moved between groups, or it
    // was never ours to move.
    // And the company file, when that is where the claim was made from.
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: ["companies"] }),
        queryClient.invalidateQueries({ queryKey: ["company"] }),
      ]),
  });
}

/**
 * What a create actually accepts — `CompanyRequest`, not `Company`.
 *
 * The two are different shapes and the schema says so since F1 turned on
 * `COMPONENT_SPLIT_REQUEST`: without it one component described both, and a
 * POST body was typed as requiring `id`, `created` and every other read-only
 * field.
 */
export type NewCompany = components["schemas"]["CompanyRequest"];

/** Screen 08, *Agregar empresa*. */
export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NewCompany) =>
      unwrap(client.POST("/api/companies/", { body }), t("errors.createCompany")),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["companies"] }),
  });
}

/**
 * The three reference tables behind screen 08's pickers.
 *
 * `staleTime: Infinity` because a *giro* is added in `/admin/` about once a
 * year, and re-fetching two hundred rows every time the modal opens is work
 * for nothing. A reload picks up a change, which is the right cadence for a
 * table nobody edits from here.
 */
const CATALOGUE_OPTIONS = { staleTime: Number.POSITIVE_INFINITY } as const;

export function useBusinessLines() {
  return useQuery({
    queryKey: ["business-lines"],
    queryFn: () =>
      unwrap(client.GET("/api/business-lines/", {}), t("errors.businessLines")),
    ...CATALOGUE_OPTIONS,
  });
}

export function useIndustries() {
  return useQuery({
    queryKey: ["industries"],
    queryFn: () => unwrap(client.GET("/api/industries/", {}), t("errors.industries")),
    ...CATALOGUE_OPTIONS,
  });
}

export function useTradeShows() {
  return useQuery({
    queryKey: ["trade-shows"],
    queryFn: () =>
      unwrap(
        // Only the ones still running: this fills a picker on a *new* company.
        // A company imported years ago still renders the name of a retired
        // show, because that comes back on the company itself.
        client.GET("/api/trade-shows/", { params: { query: { is_active: true } } }),
        t("errors.tradeShows"),
      ),
    ...CATALOGUE_OPTIONS,
  });
}

export function useCompany(id: number, enabled = true) {
  return useQuery({
    queryKey: ["company", id],
    // The event modal asks for its company's tags, and cold prospecting has no
    // company to ask about.
    enabled,
    queryFn: () =>
      unwrap(
        client.GET("/api/companies/{id}/", { params: { path: { id } } }),
        t("errors.company"),
      ),
  });
}

export function useBranches(companyId: number) {
  return useQuery({
    queryKey: ["branches", companyId],
    queryFn: () =>
      unwrap(
        client.GET("/api/branches/", {
          params: { query: { company: companyId } },
        }),
        t("errors.branches"),
      ),
  });
}

export function useCompanyContacts(companyId: number, enabled = true) {
  return useQuery({
    queryKey: ["contacts", "company", companyId],
    // The follow-up modal opens with no company chosen (cold prospecting), and
    // `/api/contacts/?branch__company=NaN` is a 400 the screen would render as
    // a failure to load contacts that were never asked for.
    enabled,
    queryFn: () =>
      unwrap(
        client.GET("/api/contacts/", {
          // `branch__company`, not `branch`: a contact hangs off a branch, and
          // asking per branch would be one request per office.
          params: { query: { branch__company: companyId } },
        }),
        t("errors.contacts"),
      ),
  });
}

export function useContact(id: number) {
  return useQuery({
    queryKey: ["contact", id],
    queryFn: () =>
      unwrap(
        client.GET("/api/contacts/{id}/", { params: { path: { id } } }),
        t("errors.contact"),
      ),
  });
}

/** Whose history to show. One or the other, never both and never neither. */
export type TimelineScope = { on: "company" | "contact"; id: number };

export function useTimeline(
  scope: TimelineScope,
  page: number,
  enabled = true,
  /** One kind only, or every kind when `null`. */
  kind: ActivityKind | null = null,
  /** Rows per page. Omitted is the API's default, `PAGE_SIZE`. */
  pageSize?: number,
) {
  return useQuery({
    queryKey: ["activities", scope, page, kind, pageSize],
    // The event modal opens on cold-prospecting rows with no company, and
    // `?company=0` is a page of somebody else's nothing.
    enabled,
    queryFn: () =>
      unwrap(
        client.GET("/api/activities/", {
          params: {
            query: {
              page,
              // Newest first. `created` is when the work happened; `due_at` is
              // when someone should be reminded, and sorting a history by a
              // reminder date interleaves the future into the past.
              ordering: "-created",
              ...(scope.on === "company"
                ? { company: scope.id }
                : { contact: scope.id }),
              ...(kind === null ? {} : { kind }),
              ...(pageSize === undefined ? {} : { page_size: pageSize }),
            },
          },
        }),
        t("errors.timeline"),
      ),
    placeholderData: keepPreviousData,
  });
}

export type NewActivity = components["schemas"]["ActivityRequest"];
export type ActivityKind = NewActivity["kind"];

/**
 * The sixteen `Activity.Kind` integers, minus the one nobody may pick.
 *
 * `DATA_IMPORT = 10` is written by the ETL and by nothing else; offering it
 * would let a seller file today's visit as an import and lose it. The design's
 * own picker omits it for the same reason (.docs/08 §3). Labels come from the
 * API as `kind_display` wherever a row is rendered; these are the ones the
 * picker needs *before* a row exists.
 */
export const ACTIVITY_KINDS: readonly ActivityKind[] = [
  0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 11, 12, 13, 14, 15,
];

/**
 * Screen 06, *Agregar seguimiento* — the most-used modal in the CRM.
 *
 * Invalidates the timeline and the prospect list: a follow-up changes the
 * *Último contacto* column, which is a company field as far as the list is
 * concerned. And the landing screen, whose day panel is the agenda's: work
 * scheduled for today from its own *Registrar seguimiento* has to land there.
 *
 * With `completes`, the follow-up written is the next step after that one, and
 * the server marks that one completed in the same request: both are saved, or
 * neither is.
 */
export function useCreateActivity(completes?: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NewActivity) =>
      unwrap(
        completes === undefined
          ? client.POST("/api/activities/", { body })
          : client.POST("/api/activities/{id}/next/", {
              params: { path: { id: completes } },
              body,
            }),
        t("errors.createActivity"),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      if (completes !== undefined) {
        void queryClient.invalidateQueries({ queryKey: ["activity", completes] });
      }
    },
  });
}

/**
 * Companies for the modal's picker, by name.
 *
 * `scope: "all"` for the same reason screen 02 uses it: a follow-up can be
 * logged against an unclaimed prospect, and the server refuses the one case
 * this must not offer — a colleague's company (.docs/08 §7 F2).
 */
export function useCompanyOptions(
  search: string,
  /**
   * `workable` leaves out what the caller may not file anything under — a
   * colleague's account, for a seller — instead of listing it greyed out.
   */
  scope: "all" | "workable" = "all",
) {
  return useQuery({
    queryKey: ["company-options", scope, search],
    // Nothing to ask until there is something to look for: the first page of
    // 25,901 companies is not a list anyone picks from.
    enabled: search.trim().length >= 2,
    queryFn: () =>
      unwrap(
        client.GET("/api/companies/", {
          // The view's own order — the seller's accounts, then colleagues',
          // then unassigned — not `name`, which is blank on many legacy rows
          // and sorted those to the top.
          params: { query: { scope, search } },
        }),
        t("errors.companies"),
      ),
    placeholderData: keepPreviousData,
  });
}

export type UpdateCompany = components["schemas"]["PatchedCompanyRequest"];

/**
 * Editing a company — *Datos generales* on design screen 04.
 *
 * A PATCH, so a form that only changes the RFC does not have to resend the
 * giros it never touched. `business_lines` and `industries` are the exception:
 * they replace what is there, which is what the chips mean.
 */
export function useUpdateCompany(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateCompany) =>
      unwrap(
        client.PATCH("/api/companies/{id}/", { params: { path: { id } }, body }),
        t("errors.saveCompany"),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["company", id] });
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

/**
 * The logo, which is the one field that cannot ride in the JSON body.
 *
 * `openapi-fetch` would serialise a `File` as `[object File]`, so this one call
 * goes through `fetch` directly with a `FormData` body — and deliberately does
 * *not* set `Content-Type`, because the browser has to add the multipart
 * boundary itself.
 */
export function useUploadCompanyLogo(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData();
      body.append("logo", file);
      const response = await fetch(`/api/companies/${id}/`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: csrfHeader(),
        body,
      });
      if (!response.ok) throw new Error(t("errors.saveCompany"));
      return (await response.json()) as Company;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["company", id] });
    },
  });
}

export type NewContact = components["schemas"]["ContactRequest"];
export type UpdateContact = components["schemas"]["PatchedContactRequest"];
export type ConsentAnswers = NonNullable<NewContact["consents"]>;

/** Screen 07, *Agregar contacto*. */
export function useCreateContact(companyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NewContact) =>
      unwrap(client.POST("/api/contacts/", { body }), t("errors.saveContact")),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contacts", "company"] });
      void queryClient.invalidateQueries({ queryKey: ["company", companyId] });
    },
  });
}

export function useUpdateContact(companyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateContact }) =>
      unwrap(
        client.PATCH("/api/contacts/{id}/", { params: { path: { id } }, body }),
        t("errors.saveContact"),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contacts", "company"] });
      void queryClient.invalidateQueries({ queryKey: ["company", companyId] });
    },
  });
}

export type NewChannels = components["schemas"]["ContactChannelsRequest"];

/**
 * A seller's one write on a contact that exists: more numbers and addresses.
 * Changing what is already there is management's — `useUpdateContact` answers
 * 403 for anyone else.
 */
export function useAddContactChannels(companyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: NewChannels }) =>
      unwrap(
        client.POST("/api/contacts/{id}/channels/", {
          params: { path: { id } },
          body,
        }),
        t("errors.saveContact"),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contacts", "company"] });
      void queryClient.invalidateQueries({ queryKey: ["contact"] });
      void queryClient.invalidateQueries({ queryKey: ["company", companyId] });
    },
  });
}

/** *Deshabilitar* and *Habilitar* on a contact card. Management's. */
export function useSetContactDisabled(companyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, disabled }: { id: number; disabled: boolean }) =>
      unwrap(
        disabled
          ? client.POST("/api/contacts/{id}/disable/", { params: { path: { id } } })
          : client.POST("/api/contacts/{id}/enable/", { params: { path: { id } } }),
        t("errors.saveContact"),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contacts", "company"] });
      void queryClient.invalidateQueries({ queryKey: ["contact"] });
      // The prospect list names a company's first contact still in use.
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
      void queryClient.invalidateQueries({ queryKey: ["company", companyId] });
    },
  });
}

/**
 * *Ya no llamar ni contactar este prospecto*, one of the two answers a
 * follow-up is completed with. One request, by the follow-up's id: its company
 * becomes *No interesado*, the timeline records that the person asked not to
 * be contacted, and the follow-up is completed — all three, or none.
 */
export function useStopContacting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      unwrap(
        client.POST("/api/activities/{id}/stop/", { params: { path: { id } } }),
        t("errors.notInterested"),
      ),
    onSuccess: (record, id) => {
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      void queryClient.invalidateQueries({ queryKey: ["activity", id] });
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
      void queryClient.invalidateQueries({ queryKey: ["company", record.company] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

export type NewBranch = components["schemas"]["BranchRequest"];

/** Screen 09, *Agregar sucursal*. */
export function useCreateBranch(companyId: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: NewBranch) =>
      unwrap(client.POST("/api/branches/", { body }), t("errors.saveBranch")),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["branches", companyId] });
      void queryClient.invalidateQueries({ queryKey: ["company", companyId] });
    },
  });
}

/**
 * Municipalities, for the branch modal's picker.
 *
 * The one catalogue too big to hold: some two and a half thousand rows, so it
 * searches server-side and only once the seller has typed enough to narrow it.
 */
export function useLocations(search: string) {
  return useQuery({
    queryKey: ["locations", search],
    enabled: search.trim().length >= 2,
    queryFn: () =>
      unwrap(
        client.GET("/api/locations/", { params: { query: { search } } }),
        t("errors.locations"),
      ),
    placeholderData: keepPreviousData,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export type DashboardSummary = components["schemas"]["DashboardSummary"];

/** *Mi vista / Equipo* on design screen 01. `team` is honoured for managers. */
export type DashboardScope = "mine" | "team";

/**
 * Design screen 01, in one request.
 *
 * Six client queries would make the landing screen the slowest in the app, and
 * it is the one every session starts on — so the figures are counted
 * server-side and arrive together with the day's agenda.
 *
 * The response deliberately carries no sales, target or ranking: they have no
 * model (D22) and `.docs/08` §5 keeps them out of the contract rather than
 * returning zeros, so the type below cannot be used to render them by accident.
 */
export function useDashboard(scope: DashboardScope, month: string) {
  return useQuery({
    queryKey: ["dashboard", scope, month],
    queryFn: () =>
      unwrap(
        client.GET("/api/dashboard/summary/", {
          params: { query: { scope, ...(month ? { month } : {}) } },
        }),
        t("errors.dashboard"),
      ),
    // Without this the whole screen blanks on every month change, which on the
    // landing screen reads as the app falling over.
    placeholderData: keepPreviousData,
  });
}

/** Whose agenda. `all` is honoured for managers only — see `ActivityViewSet`. */
export type AgendaScope = "mine" | "all";

export type AgendaRange = {
  /** Inclusive, as an instant. The grid computes both ends from the anchor. */
  from: Date;
  to: Date;
  scope: AgendaScope;
  /** A seller's id, or empty for the whole team. Manager-only. */
  agent?: string;
};

/**
 * Rows per request on the agenda: `ActivityPagination`'s cap. A range with more
 * is read in as many pages as it takes.
 */
const AGENDA_PAGE_SIZE = 500;

/**
 * Every event in a window, for design screen 03 — all of them.
 *
 * A week or a month is drawn whole or it is drawn wrong, and a manager's team
 * month runs past any page. So the first page says how many there are and the
 * rest are asked for together: one request on an ordinary week, a handful on
 * the busiest month, and never a grid that quietly stops at row fifty.
 */
export function useAgenda(range: AgendaRange) {
  return useQuery({
    queryKey: [
      "activities",
      "agenda",
      range.from.toISOString(),
      range.to.toISOString(),
      range.scope,
      range.agent ?? "",
    ],
    queryFn: async () => {
      const read = (page: number) =>
        unwrap(
          client.GET("/api/activities/", {
            params: {
              query: {
                scope: range.scope,
                due_at__gte: range.from.toISOString(),
                due_at__lte: range.to.toISOString(),
                // `id` breaks ties. Pages are cut by offset, and rows sharing
                // a `due_at` — an imported day is all midnight — would come
                // back in a different order on each request: one row on two
                // pages, another on none.
                ordering: "due_at,id",
                page,
                page_size: AGENDA_PAGE_SIZE,
                // Pending work only. The agenda is what is left to do; a done
                // event has its place on the company's timeline, not here.
                is_completed: false,
                // Approved and waiting for approval, for sellers and managers
                // alike; never rejected. Sign-off is not in use yet, and until
                // it is, waiting for it does not take a visit off anyone's week.
                status__in: [1, 2],
                // A seller's week is what they hold. `assigned_to` only records
                // who an inbound call was first given to; it decides nothing.
                ...(range.agent ? { current_agent: Number(range.agent) } : {}),
              },
            },
          }),
          t("errors.agenda"),
        );

      const first = await read(1);
      // Counted off the page the server actually sent, not the size asked
      // for: should the cap ever come down, this still reads every page.
      const pages = first.next ? Math.ceil(first.count / first.results.length) : 1;
      const rest = await Promise.all(
        Array.from({ length: pages - 1 }, (_, index) => read(index + 2)),
      );

      // A row that moved between two reads — closed, or rescheduled into the
      // range — is drawn once.
      const seen = new Set<number>();
      const results = [first, ...rest]
        .flatMap((page) => page.results)
        .filter((row) => !seen.has(row.id) && Boolean(seen.add(row.id)));
      return { ...first, next: null, results };
    },
    placeholderData: keepPreviousData,
  });
}

export type CompletedFilters = {
  /** Local calendar days, `YYYY-MM-DD`, both inclusive. Empty is open-ended. */
  from: string;
  to: string;
  /** One kind only, or every kind when `null`. */
  kind: ActivityKind | null;
  /** The author's user id, or empty for everyone the scope reaches. */
  agent: string;
  /**
   * `all` is the whole team, for managers; `authored` is what the signed-in
   * seller wrote. Never the agenda's `mine`: that is what a seller *holds*,
   * and a claimed account brings its predecessors' work along with it.
   */
  scope: "all" | "authored";
};

/**
 * The query both halves of *Actividades* send — the list and its summary.
 *
 * One function, so the band can never count a different set of rows from the
 * one the list pages through. Dated by `created`, when the row was written,
 * from local midnight to the last instant of the day: "hasta el 30" includes
 * the afternoon of the 30th in the seller's zone.
 *
 * By authorship on both sides: `agent` matches `created_by_agent` on the
 * server, and a seller's own `authored` scope reads the same column — so the
 * seller a manager picks here shows exactly the rows that seller sees.
 */
function completedQuery(filters: CompletedFilters) {
  return {
    scope: filters.scope,
    is_completed: true,
    ...(filters.from
      ? { created__gte: new Date(`${filters.from}T00:00`).toISOString() }
      : {}),
    ...(filters.to
      ? { created__lte: new Date(`${filters.to}T23:59:59.999`).toISOString() }
      : {}),
    ...(filters.kind === null ? {} : { kind: filters.kind }),
    ...(filters.agent ? { agent: Number(filters.agent) } : {}),
  };
}

/**
 * Completed work, newest first — the *Actividades* screen.
 *
 * The agenda's mirror: the same endpoint and the same scoping, but done work
 * only and read by page rather than by week.
 */
export function useCompletedActivities(filters: CompletedFilters, page: number) {
  return useQuery({
    queryKey: ["activities", "completed", filters, page],
    queryFn: () =>
      unwrap(
        client.GET("/api/activities/", {
          params: {
            query: { ...completedQuery(filters), ordering: "-created", page },
          },
        }),
        t("errors.activities"),
      ),
    placeholderData: keepPreviousData,
  });
}

export type ActivitySummary = components["schemas"]["ActivitySummary"];

/**
 * The same rows, counted by kind, for the band above the list.
 *
 * Its own request because the list is paged: counting the fifty rows in hand
 * would be a tally of a page, not of the range. No `page` in the key, so
 * paging through the list does not re-count it. Under `activities`, so marking
 * an event pending from the detail modal refreshes the band with the list.
 */
export function useCompletedSummary(filters: CompletedFilters) {
  return useQuery({
    queryKey: ["activities", "completed-summary", filters],
    queryFn: () =>
      unwrap(
        client.GET("/api/activities/summary/", {
          params: { query: completedQuery(filters) },
        }),
        t("errors.activitiesSummary"),
      ),
    placeholderData: keepPreviousData,
  });
}

/** One event, for the detail modal of screen 05. */
export function useActivity(id: number, enabled = true) {
  return useQuery({
    queryKey: ["activity", id],
    enabled,
    queryFn: () =>
      unwrap(
        client.GET("/api/activities/{id}/", { params: { path: { id } } }),
        t("errors.activity"),
      ),
  });
}

/**
 * Mark an event done, or authorise it.
 *
 * Both are ordinary field writes — `is_completed` always was, and `status`
 * since F2, where it became writable by `MANAGER_ROLES` only. A seller who
 * sends one gets a 403, which is why the button that sends it renders for a
 * manager alone. And they are the only two: a follow-up is a record once
 * written, and the API refuses any other field by name.
 */
export function useUpdateActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: components["schemas"]["PatchedActivityUpdateRequest"];
    }) =>
      unwrap(
        client.PATCH("/api/activities/{id}/", { params: { path: { id } }, body }),
        t("errors.saveActivity"),
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["activities"] });
      void queryClient.invalidateQueries({ queryKey: ["activity", variables.id] });
      void queryClient.invalidateQueries({ queryKey: ["companies"] });
      // Done or authorised, it changes what today's panel lists and counts.
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
  });
}

/** The legacy status integers, as the API enumerates them. 3 was never used. */
const COMPANY_STATUSES: readonly number[] = [1, 2, 4, 5, 6, 7, 8, 9, 10];

/**
 * Validate a status straight off the URL bar before it reaches a query string.
 *
 * "Validate input at system boundaries" applies to the address bar too: a
 * hand-edited `?status=banana` would otherwise become `status=NaN` and come
 * back as a 400 the SPA renders as a generic failure.
 */
export function statusFromParam(raw: string | null): string {
  if (!raw) return "";
  return COMPANY_STATUSES.includes(Number(raw)) ? raw : "";
}

/**
 * "No me llames", recorded in one click (.docs/06 §4).
 *
 * The server decides the source and who recorded it; the only thing the SPA
 * sends is the number and an optional note. Sending it twice is not an error —
 * a double-click means the agent could not tell the first one landed.
 */
export function useSuppressPhone() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ phone, note }: { phone: string; note?: string }) =>
      unwrap(
        client.POST("/api/suppressions/", {
          body: { phone, ...(note ? { note } : {}) },
        }),
        t("errors.suppress"),
      ),
    // The contact's phones carry `is_suppressed`, and the dial button is drawn
    // from it. Without this the agent records the request and is still shown a
    // button that now 400s.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contact"] }),
  });
}
